import { useContext, useState } from 'react';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import CompanyListingService from './CompanyListingService';

const ATTRIBUTE_OPTIONS = [
    { key: 'fortune_five_hundred', label: 'Fortune 500' },
    { key: 'startup',              label: 'Startup'      },
    { key: 'unicorn_status',       label: 'Unicorn'      },
    { key: 'mnc_status',           label: 'MNC'          },
    { key: 'listed_status',        label: 'Listed'       },
    { key: 'is_defence',           label: 'Defence'      },
    { key: 'is_prestigious',       label: 'Prestigious'  },
    // { key: 'tier_one',             label: 'Tier 1'       },
    { key: 'is_registered_company',       label: 'Registered Company' },
    { key: 'is_indian_government',        label: 'Indian Government' },
    { key: 'is_international_government', label: 'International Government' },
    { key: 'is_international_body',       label: 'International Body' },
    { key: 'is_law_firm',                 label: 'Law Firm' },
    { key: 'is_courts_tribunals',         label: 'Courts and Tribunals' },
    { key: 'is_educational_institute',    label: 'Educational Institute' },
    { key: 'is_healthcare_organization',  label: 'Healthcare Organization' },
    { key: 'is_ngo',                      label: 'NGO' },
];

interface SearchCriteriaProps {
    searchCriteria: any;
    setSearchCriteria: (criteria: any) => void;
    setCompanyList: (list: any[]) => void;
}

const SearchCriteria = ({ searchCriteria, setSearchCriteria, setCompanyList }: SearchCriteriaProps) => {
    const { globalState } = useContext(GlobalContext);
    const countryDomain: any[] = globalState?.domain?.get('COUNTRY_CODE') ?? [];
    const topCountries = countryDomain.filter((item) => item.displayOrder > 0).sort((a, b) => a.displayOrder - b.displayOrder);
    const topCountryCodes = new Set(topCountries.map((item) => item.code));
    const otherCountries = countryDomain.filter((item) => !topCountryCodes.has(item.code));
    const industryDomain: any[] = globalState?.domain?.get('INDUSTRY_CODE') ?? [];

    const [localCriteria, setLocalCriteria] = useState({ ...searchCriteria });

    const handleChange = (key: string, value: string) => {
        setLocalCriteria((prev: any) => ({ ...prev, [key]: value }));
    };

    const handleAttributeToggle = (key: string, checked: boolean) => {
        const current: string[] = localCriteria.attributes ?? [];
        const updated = checked
            ? [...current, key]
            : current.filter((k: string) => k !== key);
        setLocalCriteria((prev: any) => ({ ...prev, attributes: updated }));
    };

    const handleSearch = async () => {
        setSearchCriteria(localCriteria);
        try {
            const response = await CompanyListingService.search(localCriteria);
            setCompanyList(response.data ?? []);
        } catch (error) {
            console.error('Search error:', error);
        }
    };

    const handleClear = async () => {
        const defaultCriteria = CompanyListingService.getDefaultSearchCriteria();
        setLocalCriteria(defaultCriteria);
        setSearchCriteria(defaultCriteria);
        try {
            const response = await CompanyListingService.search(defaultCriteria);
            setCompanyList(response.data ?? []);
        } catch (error) {
            console.error('Clear error:', error);
        }
    };

    const selectedAttributes: string[] = localCriteria.attributes ?? [];

    return (
        <div className="fitergroup mb-3">
            <div className="accordion-collapse collapse show">
                <div className="accordion-body p-4">
                    <div className="row g-3">

                         {/* Company Attributes */}
                        <div className="col-md-12">
                            <label className="form-label font-14 font-500 d-block mb-2">
                                Company Attributes
                            </label>
                            <div className="d-flex flex-wrap gap-3">
                                {ATTRIBUTE_OPTIONS.map(({ key, label }) => (
                                    <div className="form-check" key={key}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`filter_${key}`}
                                            checked={selectedAttributes.includes(key)}
                                            onChange={(e) => handleAttributeToggle(key, e.target.checked)}
                                        />
                                        <label
                                            className="form-check-label font-13"
                                            htmlFor={`filter_${key}`}>
                                            {label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Country */}
                        <div className="col-md-3">
                            <label className="form-label font-14 font-500">Country</label>
                            <select
                                className="form-select"
                                value={localCriteria.country ?? ''}
                                onChange={(e) => handleChange('country', e.target.value)}>
                                <option value="">Select</option>
                                {topCountries.length > 0 && (
                                    <optgroup label="Top Countries">
                                        {topCountries.map((item) => (
                                            <option key={`top-${item.code}`} value={item.code}>{item.value}</option>
                                        ))}
                                    </optgroup>
                                )}
                                <optgroup label="All Countries">
                                    {otherCountries.map((item) => (
                                        <option key={item.code} value={item.code}>{item.value}</option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>

                        {/* Industry */}
                        <div className="col-md-3">
                            <label className="form-label font-14 font-500">Industry</label>
                            <select
                                className="form-select"
                                value={localCriteria.industry_id ?? ''}
                                onChange={(e) => handleChange('industry_id', e.target.value)}>
                                <option value="">Select</option>
                                {industryDomain.map((item: any) => (
                                    <option key={item.code} value={item.code}>{item.value}</option>
                                ))}
                            </select>
                        </div>

                        {/* Company Size */}
                        <div className="col-md-3">
                            <label className="form-label font-14 font-500">Company Size</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. 500"
                                value={localCriteria.company_size ?? ''}
                                onChange={(e) => handleChange('company_size', e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                        </div>

                        {/* Search / Clear buttons */}
                        <div className="col-md-3 d-flex align-items-end gap-2">
                            <button className="btn btn-green" onClick={handleSearch}>
                                <span>Search</span>
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleClear}>
                                Clear
                            </button>
                        </div>

                       

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchCriteria;
